import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  SimpleChanges,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TaggingService } from '@app-asset-config/services/tagging.service';
import { LoginFleetInfo } from '@app-core/models/core.model';
import { AccessService } from '@app-core/services/access/access.service';
import { DataService } from '@app-core/services/data/data.service';
import { UpdateTags } from '@app-shared/actions/tags.action';
import { State } from '@app-shared/reducers';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { getClientConfig } from '@config/client-config';
import { DurationRangeService } from '@app-core/services/duration-range/duration-range.service';
import { TagsService } from '@app-shared/services/tags/tags.service';

@Component({
  selector: 'app-tagging-filter-v2',
  templateUrl: './tagging-filter-v2.component.html',
  styleUrls: ['./tagging-filter-v2.component.scss'],
})
export class TaggingFilterV2Component implements OnInit, OnChanges, OnDestroy {
  @Input() entityType = '';
  @Input() dropDownType = 'assign';
  @Input() public assignedTagList = [];
  @Input() public numOfTagsToDisplay = 1;
  @Input() attributeType = '';
  @Input() roleId = '';
  @Input() isClearDropDown = false;
  @Input() isUserPage = false;
  @Input() appearance = '';
  @Input() styles: any = {};
  @Input() label: string = '';
  @Input() isWideFeild: boolean = false;
  @Input() public filterType: 'flat' | 'box' = 'flat';
  @Input() private readonly filterOutAccessTags: boolean = false;
  @Input() isReactMicrofrontend: boolean;

  @ViewChild('mySelect') mySelect;
  @ViewChild('multiUserSearch') multiUserSearch;

  @Output() public selectedTags: EventEmitter<any> = new EventEmitter();
  @Output() public dropdownClosed: EventEmitter<void> = new EventEmitter();
  public tags = new FormControl([], this.maxTagsValidator());
  public isEditMode = false;
  public getAllTagsLoader = false;
  public allAttrTagList = [];
  public filteredAttribute: any[] = [];
  public tagList = [];
  public ids = [];
  public totalTags = 0;
  private ngUnsubscribeOnChanges: Subject<void> = new Subject<void>();
  public tagIds: any[];
  public allTags: any[];

  private currentFleetId = '';
  private currentFleetInfo: LoginFleetInfo;
  private allowedUserTags = [];
  public readonly env = getClientConfig().environments;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private taggingService: TaggingService,
    private accessService: AccessService,
    private dataService: DataService,
    private translate: TranslateService,
    private store: Store<State>,
    private cdRef: ChangeDetectorRef,
    private durationRangeService: DurationRangeService,
    private tagMapper: TagsService,
    public durationService: DurationRangeService
  ) {}

  async ngOnInit() {
    const { fleets = [] } = this.accessService.getLoginInfo();
    this.currentFleetId = this.dataService._currentFleet.getValue();
    this.currentFleetInfo = fleets.filter((x: any) => x.fleetId === this.currentFleetId)[0];
    await this.getTags();

    this.tags.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: any) => {
      this.tagList = [];
      const selectedAccessTags = [];
      value?.map((tagTd) => {
        this.allAttrTagList?.map((attr) => {
          attr?.tags?.map((tag) => {
            if (parseInt(tag?.tagId) === parseInt(tagTd)) {
              this.tagList.push(tag?.tagName);
              if (attr?.attributeType === 'ACCESS') {
                selectedAccessTags.push(tag);
              }
            }
          });
        });
      });

      this.reorderTags();
      this.cdRef.detectChanges();
      this.selectedTags.emit(value);
      if (this.multiUserSearch) {
        this.multiUserSearch.nativeElement.value = '';
        this.onInputChange({ target: this.multiUserSearch.nativeElement });
      }
    });

    this.dataService._currentFleet.pipe(skip(1), takeUntil(this.ngUnsubscribe)).subscribe(async (value: string) => {
      if (value) {
        this.currentFleetId = value;
        this.currentFleetInfo = fleets.filter((x: any) => x.fleetId === this.currentFleetId)[0];
        await this.getTags();
      }
    });

    this.assignSelectedDropdownValue();

    this.tags.valueChanges.subscribe((tagIds: string[]) => {
      const selectedTags = this.tagMapper.mapTagIdsToTags(tagIds, this.filteredAttribute);

      window.dispatchEvent(new CustomEvent('tags:selected', { detail: selectedTags }));
    });
  }

  public async ngOnChanges(changes: SimpleChanges) {
    try {
      if (changes.assignedTagList && changes.assignedTagList.currentValue) {
        this.assignedTagList.length &&
          this.assignedTagList.forEach((assignedTag) => {
            const tagId = assignedTag.tagId;
            const matchingTag = this.filteredAttribute.find((tag) => tag?.tags?.some((t) => t?.tagId === tagId));

            if (matchingTag) {
              const matchingTagData = matchingTag.tags.find((t) => t.tagId === tagId);
              Object.assign(assignedTag, matchingTagData);
            }
          });

        this.assignSelectedDropdownValue();
      }
    } catch (error) {
      console.error(error);
    }

    if (changes.isClearDropDown && changes.isClearDropDown.currentValue) {
      if (this.isClearDropDown) {
        this.tagList = [];
        this.tags.patchValue([]);

        if (this.multiUserSearch) {
          this.multiUserSearch.nativeElement.value = '';
        }
        this.filteredAttribute = [...this.allAttrTagList];
      }
    }
  }

  public ngOnDestroy() {
    this.ngUnsubscribeOnChanges.next();
    this.ngUnsubscribeOnChanges.complete();
  }

  onInputChange(event: any) {
    const searchInput = (event.target.value || '').trim().toLowerCase();
    const selectedTagIds = (this.tags.value || []).map((id) => +id);

    this.filteredAttribute = this.allAttrTagList
      .map((attr) => {
        if (!attr.tags) return null;

        const filteredTags = attr.tags
          .map((tag) => {
            const tagNameLower = (tag.tagName || '').toLowerCase();

            if (tagNameLower.includes(searchInput) || selectedTagIds.includes(+tag.tagId)) {
              const highlightedName = tagNameLower.includes(searchInput)
                ? tag.tagName.replace(new RegExp(`(${searchInput})`, 'gi'), '<b>$1</b>')
                : tag.tagName;

              return { ...tag, highlightedName };
            }
            return null;
          })
          .filter(Boolean);

        if (filteredTags.length) {
          return { ...attr, tags: filteredTags };
        }
        return null;
      })
      .filter(Boolean);

    this.reorderTagsFiltered();
  }

  private reorderTagsFiltered(): void {
    if (!this.filteredAttribute?.length) return;

    const selectedTagIds = (this.tags.value || []).map((id) => +id);

    const selectedTags: any[] = [];
    const updatedGroups = this.filteredAttribute.map((attr) => {
      if (!attr.tags?.length) return attr;

      const selected = attr.tags.filter((t) => selectedTagIds.includes(+t.tagId)).map((t) => ({ ...t, attributeType: attr.attributeType }));
      const unselected = attr.tags.filter((t) => !selectedTagIds.includes(+t.tagId));

      selectedTags.push(...selected);

      return {
        ...attr,
        tags: unselected,
      };
    });

    this.filteredAttribute = [
      ...selectedTags.sort((a, b) => a.tagName.localeCompare(b.tagName)),
      ...updatedGroups.filter((group) => group?.tags?.length),
    ];

    const selectedAccessTags = selectedTags.filter((t) => t.attributeType === 'ACCESS');
    const disableLastAccessTag = this.allowedUserTags.length > 0 && selectedAccessTags.length === 1;

    this.filteredAttribute.forEach((attr: any) => {
      if (attr?.tags?.length) {
        attr.tags.forEach((tag) => {
          tag.disabled = disableLastAccessTag && tag?.tagId === selectedAccessTags[0]?.tagId;
        });
      } else if (attr?.tagId) {
        attr.disabled = disableLastAccessTag && attr.tagId === selectedAccessTags[0]?.tagId;
      }
    });
  }

  onOpenChange(searchInput: any, isOpen?: boolean) {
    if (!searchInput) {
      return;
    }
    if (isOpen) {
      searchInput.value = '';
      this.filteredAttribute = [...this.allAttrTagList];
      this.reorderTags();
    } else {
      this.dropdownClosed.emit();
    }
  }

  public editMode() {
    this.isEditMode = true;
  }

  public doneMode() {
    this.isEditMode = false;
  }

  private maxTagsValidator() {
    const limit: number = this.durationRangeService.maxAllowedTags;
    return (control: FormControl) => {
      return control.value && control.value.length > limit ? { maxTagsLimit: true, maxAllowedTags: limit } : null;
    };
  }

  private async getTags(): Promise<any> {
    try {
      this.getAllTagsLoader = true;
      this.totalTags = 0;
      const params: any = {
        limit: 500,
        offset: 0,
      };
      if (this.entityType) {
        params['entityNames[]'] = this.entityType;
      }
      if (this.roleId) {
        params['roleIds[]'] = this.roleId;
      }
      if (this.attributeType) {
        params['attributeType'] = this.attributeType;
      }

      const res = await this.taggingService.getAttributes(params).toPromise();
      if (Boolean(this.env.isGeotabEnv)) {
        const validAttributeTypes = ['DATA', 'ACCESS'];
        res.data = (res?.data || []).map((attr) => {
          if (!validAttributeTypes.includes(attr.attributeType)) {
            attr.attributeType = 'ACCESS';
          }
          return attr;
        });
        if (this.filterOutAccessTags) {
          res.data = (res?.data || []).filter((attr) => attr.attributeType === 'ACCESS');
        }
      }
      const formattedData = (res?.data || [])
        .map((attr) => {
          const { entities = [], tags = [] } = attr;
          this.totalTags = this.totalTags + (tags?.length || 0);
          if ((entities || []).length < 2) {
            return [attr];
          }
          const splitAttr = (entities || []).map((e) => {
            return {
              ...attr,
              optionGroupLabel: `${attr.attributeName} (${e})`,
              tags: (tags || []).filter((t) => t.entityName === e),
            };
          });
          return splitAttr;
        })
        .reduce((a, b) => [...a, ...b], []);

      formattedData.forEach((attr) => {
        if (attr.tags) {
          attr.tags.sort((a, b) => a.tagName.localeCompare(b.tagName));
        }
      });

      const [dataBasedAttrs, accessBasedAttrs] = (formattedData || []).reduce(
        (acc, attr) => {
          if (attr.attributeType === 'DATA') {
            acc[0].push(attr);
          } else {
            acc[1].push(attr);
          }
          return acc;
        },
        [[], []]
      );

      const userAttrIds = [...new Set((this.currentFleetInfo?.userTags || []).map((t) => t.attributeId))];
      const userTagIds = (this.currentFleetInfo?.userTags || []).map((t) => t.tagId);
      let allowedAccessBasedAttrs = [];

      if (userAttrIds.length) {
        allowedAccessBasedAttrs = accessBasedAttrs
          .filter((a) => userAttrIds.includes(a.attributeId))
          .map((a) => {
            const allowedTags = (a.tags || []).filter((t) => userTagIds.includes(t.tagId));
            this.allowedUserTags.push(...allowedTags);
            return {
              ...a,
              tags: allowedTags,
            };
          });
        if (!this.assignedTagList.length) {
          this.assignedTagList = [...this.allowedUserTags];
        }
      } else {
        allowedAccessBasedAttrs = [...accessBasedAttrs];
      }

      this.allAttrTagList = [...dataBasedAttrs, ...allowedAccessBasedAttrs];
      this.getAllTagsLoader = false;
      this.filteredAttribute = this.allAttrTagList;
      if (!this.isUserPage) {
        this.assignSelectedDropdownValue();
      }
      const allTags = this.filteredAttribute;
      this.store.dispatch(new UpdateTags({ allTags }));
      return allTags;
    } catch (error) {
      this.getAllTagsLoader = false;
      throw error;
    }
  }

  public assignSelectedDropdownValue() {
    this.ids = [];
    this.assignedTagList?.map((tag) => {
      this.ids.push(tag?.tagId);
    });
    this.tags.patchValue(this.ids);

    if (this.multiUserSearch) {
      this.multiUserSearch.nativeElement.value = '';
    }
  }

  getTooltip(tag: any): string | null {
    if (tag?.tagStatus === 'INACTIVE') {
      return this.translate.instant('DisabledTagToolTip');
    }
    return null;
  }

  public unselectTags() {
    const selectedTagIds = this.tags.value || [];
    const selectedAccessTags = this.allowedUserTags.filter((t) => selectedTagIds.includes(t.tagId));

    if (this.allowedUserTags.length) {
      const keepTag = selectedAccessTags[0] || this.allowedUserTags[0];
      this.tags.patchValue([keepTag.tagId]);
      this.selectedTags.emit([keepTag.tagId]);

      this.filteredAttribute.forEach((attr: any) => {
        if (attr?.tags?.length) {
          attr.tags.forEach((tag) => {
            tag.disabled = tag?.tagId === keepTag.tagId;
          });
        } else if (attr?.tagId) {
          attr.disabled = attr.tagId === keepTag.tagId;
        }
      });
    } else {
      this.tags.patchValue([]);
      this.selectedTags.emit([]);
    }

    if (this.multiUserSearch) {
      this.multiUserSearch.nativeElement.value = '';
    }
    this.filteredAttribute = [...this.allAttrTagList];
    this.reorderTags();
  }

  private reorderTags(): void {
    if (!this.allAttrTagList?.length) return;

    const selectedTagIds = (this.tags.value || []).map((id) => +id);

    const selectedTags: any[] = [];
    const updatedGroups = this.allAttrTagList.map((attr) => {
      if (!attr.tags?.length) return attr;

      const selected = attr.tags.filter((t) => selectedTagIds.includes(+t.tagId)).map((t) => ({ ...t, attributeType: attr.attributeType }));
      const unselected = attr.tags.filter((t) => !selectedTagIds.includes(+t.tagId));

      selectedTags.push(...selected);

      return {
        ...attr,
        tags: unselected,
      };
    });

    this.filteredAttribute = [
      ...selectedTags.sort((a, b) => a.tagName.localeCompare(b.tagName)),
      ...updatedGroups.filter((group) => group?.tags?.length),
    ];

    const selectedAccessTags = selectedTags.filter((t) => t.attributeType === 'ACCESS');
    const disableLastAccessTag = this.allowedUserTags.length > 0 && selectedAccessTags.length === 1;

    this.filteredAttribute.forEach((attr: any) => {
      if (attr?.tags?.length) {
        attr.tags.forEach((tag) => {
          tag.disabled = disableLastAccessTag && tag?.tagId === selectedAccessTags[0]?.tagId;
        });
      } else if (attr?.tagId) {
        attr.disabled = disableLastAccessTag && attr.tagId === selectedAccessTags[0]?.tagId;
      }
    });
  }
}
